import React, { useMemo, useState } from 'react';
import { Container, Row, Col, Form, Card, Alert } from 'react-bootstrap'; // Ensure Alert is imported
import { FaGraduationCap, FaClipboardList, FaCogs } from 'react-icons/fa';

// Generic components and hooks
import GenericCrudPage from '../components/GenericCrudPage';
import { useDegrees, useComponentMasters } from '../hooks/useDropdownData';

const BASE_URL = process.env.REACT_APP_BASE_URL;

// --- Configuration and Helper Functions (Defined Globally) ---

// Validation for the form
const validateForm = (form) => {
  const errors = {};
  if (!form.exEduCourseTypeName?.trim()) {
    errors.exEduCourseTypeName = 'Course Type Name is required.';
  }
  if (!form.degree) {
    errors.degree = 'Degree is required.';
  }
  if (!form.componentMaster || form.componentMaster.length === 0) {
    errors.componentMaster = 'At least one Component Master is required.';
  }
  return errors;
};

// Transform form data to API payload
const transformPayload = (form, editId, userId) => {
  return {
    exEduCourseTypeName: form.exEduCourseTypeName?.trim(),
    degree: parseInt(form.degree, 10),
    componentMaster: form.componentMaster.map(id => parseInt(id, 10)),
    exIsActive: parseInt(form.exIsActive, 10),
    user: userId,
    ...(editId && { id: editId }),
  };
};

// Transform API data to form state for editing
const transformToForm = (item) => ({
  exEduCourseTypeName: item.exEduCourseTypeName || '',
  degree: item.degreeid?.toString() || '',
  componentMaster: (item.compnentMasterId || []).map(id => id.toString()),
  exIsActive: item.isactive ? 1 : 0,
});

// Define view modal fields
const getViewFields = (item) => item ? [
  { label: 'Course Type Name', value: item.exEduCourseTypeName, icon: FaClipboardList },
  { label: 'Degree', value: item.degreename, icon: FaGraduationCap },
  { label: 'Component Masters', value: item.compnentMasterName?.join(', ') || 'N/A', icon: FaCogs }
] : [];

// Table columns definition
const tableColumns = [
  { key: 'exEduCourseTypeName', label: 'Name', sortable: true },
  { key: 'degreename', label: 'Degree', sortable: true },
  {
    key: 'compnentMasterName',
    label: 'Component Masters',
    render: (item) => (
      Array.isArray(item.compnentMasterName) && item.compnentMasterName.length > 0
        ? item.compnentMasterName.join(', ')
        : '-'
    )
  },
  {
    key: 'isactive',
    label: 'Status',
    render: (item) => (
      <span className={`badge ${item.isactive ? 'bg-success' : 'bg-secondary'}`}>
        {item.isactive ? 'Active' : 'Inactive'}
      </span>
    )
  },
];

// CSV headers definition
const csvHeaders = [
  { key: 'exEduCourseTypeName', label: 'Course Type Name' },
  { key: 'degreename', label: 'Degree' },
  { key: 'compnentMasterName', label: 'Component Masters', render: (item) => item.compnentMasterName?.join(', ') || '' },
  { key: 'isactive', label: 'Status', render: (item) => item.isactive ? 'Active' : 'Inactive' }
];

// --- Edu Course Type Page Component ---
export default function EduCourseTypePage() {
  const { degrees, loading: loadingDegrees } = useDegrees();
  const [selectedDegree, setSelectedDegree] = useState(''); // State for the filter dropdown
  const { componentMasters, loading: loadingComponentMasters } = useComponentMasters(selectedDegree);

  // CRUD configuration object
  const crudConfig = useMemo(() => ({
    title: 'Edu Course Type',
    entityName: 'Edu Course Type',
    baseUrl: BASE_URL,
    endpoints: {
      fetch: '/masters/getEduCoursesType',
      save: '/masters/createEduCoursesType',
      update: '/masters/updateEduCoursesType',
      delete: '/masters/deleteEduCoursesType'
    },
    fetchMethod: 'POST',
    deleteMethod: 'POST',
    fetchPayload: {
      degree: Number(selectedDegree) || 0,
    },
    deletePayload: {
      user: 1
    },
    initialFormState: {
      exEduCourseTypeName: '',
      degree: selectedDegree,
      componentMaster: [],
      exIsActive: 1,
    },
    validateForm: validateForm // This is correctly referencing the global function
  }), [selectedDegree]);

  // Form fields definition
  const formFields = useMemo(() => {
    const isComponentMasterDisabled = !selectedDegree;
    const componentMasterDescription = isComponentMasterDisabled
      ? 'Please select a Degree first to load available Component Masters.'
      : null;

    return [
      { name: 'exEduCourseTypeName', label: 'Course Type Name', icon: FaClipboardList, required: true, placeholder: 'Enter course type name' },
      {
        name: 'degree',
        label: 'Degree',
        icon: FaGraduationCap,
        as: 'select',
        required: true,
        options: [
          { value: '', label: loadingDegrees ? 'Loading...' : '-- Select Degree --' },
          ...degrees.map(d => ({ value: d.id, label: d.exName }))
        ],
      },
      {
        name: 'componentMaster',
        label: 'Component Masters',
        icon: FaCogs,
        as: 'multiselect',
        required: true,
        options: componentMasters.map(c => ({ value: c.id, label: c.exComponentName || c.exName })),
        loading: loadingComponentMasters,
        disabled: isComponentMasterDisabled,
        description: componentMasterDescription, // Add this description
      },
      {
        name: 'exIsActive',
        label: 'Status',
        as: 'select',
        options: [
          { value: 1, label: 'Active' },
          { value: 0, label: 'Inactive' }
        ]
      }
    ];
  }, [degrees, componentMasters, loadingDegrees, loadingComponentMasters, selectedDegree]);


  return (
    <Container fluid className="py-4 px-lg-5 bg-light-subtle min-vh-100">
      <div className="mb-4">
        <h2 className="fw-bolder page-title mb-2">Edu Course Type Master</h2>
        <p className="text-muted">Manage educational course types and their associated components.</p>
      </div>

      <Card className="mb-4 border-0 shadow-sm">
        <Card.Body>
          <Row className="align-items-center">
            <Col md={3}>
              <div className="d-flex align-items-center">
                <FaGraduationCap className="text-primary me-2" />
                <span className="fw-bold text-muted">Filter by Degree:</span>
              </div>
            </Col>
            <Col md={9}>
              <Form.Select
                value={selectedDegree}
                onChange={(e) => setSelectedDegree(e.target.value)}
                disabled={loadingDegrees}
                className="form-select-lg"
              >
                <option value="">
                  {loadingDegrees ? 'Loading degrees...' : '-- Select a Degree to View Course Types --'}
                </option>
                {degrees?.map((degree) => (
                  <option key={degree.id} value={degree.id}>
                    {degree.exName}
                  </option>
                )) || []}
              </Form.Select>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {selectedDegree ? (
        <GenericCrudPage
          title="Edu Course Type"
          config={crudConfig}
          formFields={formFields}
          // Pass the globally defined variables/functions directly
          tableColumns={tableColumns}
          csvHeaders={csvHeaders}
          transformPayload={transformPayload}
          transformToForm={transformToForm}
          getViewFields={getViewFields}
          emptyIcon={FaClipboardList}
          searchPlaceholder="Search by course type name, degree, or components..."
        />
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <FaGraduationCap size={64} className="text-muted mb-4 opacity-50" />
            <h4 className="text-muted mb-3">Select a Degree Program</h4>
            <p className="text-muted mb-0">
              Please select a degree from the dropdown above to view and manage its educational course types.
              <br />
              <small>Course types define the structure and components within each degree program.</small>
            </p>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
}